import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, scrollToId } from "@/lib/site";
import { copy } from "@/lib/content";
import { Wordmark } from "./Logo";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="language-toggle" className="relative flex rounded-full border border-amber-200/20 p-1 font-mono text-xs">
      {["id", "en"].map((l) => (
        <button key={l} data-testid={`lang-${l}-button`} onClick={() => setLang(l)}
          className={`relative z-10 px-3 py-1 uppercase tracking-widest transition-colors duration-300 ${lang === l ? "text-[#070A12]" : "text-slate-300 hover:text-amber-200"}`}>
          {lang === l && <motion.span layoutId="lang-pill" className="absolute inset-0 -z-10 rounded-full bg-amber-300" />}
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { lang } = useLang();
  const t = copy[lang];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 border-b ${scrolled ? "bg-[#070A12]/75 backdrop-blur-xl border-amber-200/10 py-3" : "border-transparent py-5"}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <button data-testid="brand-logo" onClick={() => go("beranda")} className="group text-left"><Wordmark /></button>
        <nav className="hidden lg:flex items-center gap-9">
          {t.nav.map(([id, label]) => (
            <button key={id} data-testid={`nav-${id}-link`} onClick={() => go(id)} className="nav-link text-sm text-slate-300 hover:text-slate-50 transition-colors">{label}</button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <button data-testid="nav-cta-button" onClick={() => go("ebook")} className="hidden md:inline-flex btn-gold rounded-full px-5 py-2.5 text-sm font-semibold">{t.navCta}</button>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden rounded-full border border-amber-200/20 p-2.5 text-slate-100">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav data-testid="mobile-menu" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}
            className="lg:hidden mx-5 mt-3 rounded-2xl border border-amber-200/15 bg-[#0B1020]/95 backdrop-blur-xl p-4">
            {t.nav.map(([id, label], i) => (
              <button key={id} data-testid={`mobile-nav-${id}-link`} onClick={() => go(id)} className="flex w-full items-baseline gap-4 border-b border-white/5 py-3 text-left last:border-0">
                <span className="font-mono text-xs text-amber-300">0{i + 1}</span>
                <span className="font-display text-2xl text-slate-50">{label}</span>
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
